import { useId } from "react";
import { IconoError, IconoCheck, IconoChevronAbajo } from "./Iconos";
import "./kit.css";

/* ============================================================================
   CAMPOS · una sola forma de resolver etiqueta, obligatoriedad y error.
   Hoy hay 59 archivos con `form-control` a mano donde cada uno decide dónde va
   la etiqueta y cómo se ve un error.

   Reglas del sistema:
   · Etiqueta ARRIBA, siempre. El placeholder es un ejemplo, nunca la etiqueta.
   · El asterisco marca lo obligatorio.
   · El error va DEBAJO del campo, con icono, y el borde se pone rojo.
   · El campo queda asociado a su etiqueta y a su error por id, para que un
     lector de pantalla los lea juntos.

   Anatomía literal: alto 38px, radio 7px, padding 0 11px, fuente 14px, gap 9px;
   etiqueta 10px/500 mayúsculas con letter-spacing .08em.
   ========================================================================== */

export function Campo({ etiqueta, obligatorio = false, error, ayuda, children, htmlFor, className = "", etiquetaOculta = false }) {
    const idError = htmlFor ? `${htmlFor}-error` : undefined;
    return (
        <div className={`kc${error ? " kc--error" : ""}${className ? ` ${className}` : ""}`}>
            {etiqueta && (
                // Un input dentro de una celda de tabla no puede llevar la
                // etiqueta encima -- pero SIGUE necesitando nombre accesible,
                // así que se oculta a la vista, no al lector de pantalla.
                <label className={`kc__etiqueta${etiquetaOculta ? " kc__etiqueta--oculta" : ""}`} htmlFor={htmlFor}>
                    {etiqueta}
                    {obligatorio && (
                        <span className="kc__obligatorio" aria-hidden="true">
                            {" *"}
                        </span>
                    )}
                </label>
            )}
            {children}
            {error ? (
                <span className="kc__error" id={idError} role="alert">
                    <IconoError size={13} grosor={2.2} />
                    {error}
                </span>
            ) : (
                ayuda && <span className="kc__ayuda">{ayuda}</span>
            )}
        </div>
    );
}

/** Caja del control: es lo que lleva el borde, el foco y el estado de error. */
function Caja({ icono: Icono, sufijo, area, children }) {
    return (
        <span className={`kc__caja${area ? " kc__caja--area" : ""}`}>
            {Icono && (
                <span className="kc__icono">
                    <Icono size={16} />
                </span>
            )}
            {children}
            {sufijo}
        </span>
    );
}

export function CampoTexto({ etiqueta, obligatorio, error, ayuda, icono, id, className, etiquetaOculta, ...resto }) {
    const autoId = useId();
    const idFinal = id || autoId;
    return (
        <Campo etiqueta={etiqueta} obligatorio={obligatorio} error={error} ayuda={ayuda} htmlFor={idFinal} className={className} etiquetaOculta={etiquetaOculta}>
            <Caja icono={icono}>
                <input
                    id={idFinal}
                    className="kc__control"
                    aria-invalid={error ? true : undefined}
                    aria-describedby={error ? `${idFinal}-error` : undefined}
                    {...resto}
                />
            </Caja>
        </Campo>
    );
}

export function CampoArea({ etiqueta, obligatorio, error, ayuda, id, filas = 3, className, ...resto }) {
    const autoId = useId();
    const idFinal = id || autoId;
    return (
        <Campo etiqueta={etiqueta} obligatorio={obligatorio} error={error} ayuda={ayuda} htmlFor={idFinal} className={className}>
            <Caja area>
                <textarea
                    id={idFinal}
                    rows={filas}
                    className="kc__control"
                    aria-invalid={error ? true : undefined}
                    aria-describedby={error ? `${idFinal}-error` : undefined}
                    {...resto}
                />
            </Caja>
        </Campo>
    );
}

export function CampoSelect({ etiqueta, obligatorio, error, ayuda, id, opciones = [], placeholder, className, ...resto }) {
    const autoId = useId();
    const idFinal = id || autoId;
    return (
        <Campo etiqueta={etiqueta} obligatorio={obligatorio} error={error} ayuda={ayuda} htmlFor={idFinal} className={className}>
            <Caja sufijo={<IconoChevronAbajo size={17} className="kc__icono" />}>
                <select
                    id={idFinal}
                    className="kc__control"
                    aria-invalid={error ? true : undefined}
                    aria-describedby={error ? `${idFinal}-error` : undefined}
                    {...resto}
                >
                    {placeholder && <option value="">{placeholder}</option>}
                    {opciones.map((o) => (
                        <option key={o.value} value={o.value}>
                            {o.label}
                        </option>
                    ))}
                </select>
            </Caja>
        </Campo>
    );
}

export function Casilla({ marcada = false, onChange, children, disabled = false, etiqueta }) {
    return (
        <label className={`kc-opcion${disabled ? " kc-opcion--deshabilitada" : ""}`}>
            <input
                type="checkbox"
                checked={marcada}
                onChange={(e) => onChange && onChange(e.target.checked)}
                disabled={disabled}
                // Una casilla sin texto al lado (la de una fila de tabla) no
                // tiene nombre accesible: hay que darselo.
                aria-label={!children ? etiqueta : undefined}
                style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
            />
            <span className={`kc-casilla${marcada ? " is-marcada" : ""}`} aria-hidden="true">
                {marcada && <IconoCheck size={12} grosor={3} />}
            </span>
            {children}
        </label>
    );
}

export function Opcion({ marcada = false, onChange, children, name, value, disabled = false }) {
    return (
        <label className={`kc-opcion${disabled ? " kc-opcion--deshabilitada" : ""}`}>
            <input
                type="radio"
                name={name}
                value={value}
                checked={marcada}
                onChange={() => onChange && onChange(value)}
                disabled={disabled}
                style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
            />
            <span className={`kc-radio${marcada ? " is-marcada" : ""}`} aria-hidden="true" />
            {children}
        </label>
    );
}

export function Interruptor({ encendido = false, onChange, children, disabled = false, titulo }) {
    return (
        <label className={`kc-opcion${disabled ? " kc-opcion--deshabilitada" : ""}`}>
            <input
                type="checkbox"
                role="switch"
                checked={encendido}
                aria-label={titulo}
                onChange={(e) => onChange && onChange(e.target.checked)}
                disabled={disabled}
                style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
            />
            <span className={`kc-switch${encendido ? " is-encendido" : ""}`} aria-hidden="true">
                <span className="kc-switch__bola" />
            </span>
            {children}
        </label>
    );
}

export default Campo;
